// components/ApplicationDownloadNote.tsx
import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { SuccessToast, ErrorToast } from "../toastMessage";
import { generateFilledPDFDocument } from "./DocumentGenerator";
import { useSelectedApplication } from "./ApplicationFrom.ctx";

const getTranslation = (key: string, language: string): string => {
  const translations: any = {
    english: {
      note: "Note",
      applicants_download_note:
        "Applicants are required to download the filled-in application form, sign it (self-attest), and upload the signed copy to complete the submission.",
      download_form: "Download Form",
      downloading: "Downloading...",
      save_application_first: "Please save the application first",
    },
    hindi: {
      note: "नोट",
      applicants_download_note:
        "आवेदकों को भरा हुआ आवेदन पत्र डाउनलोड करना, उस पर हस्ताक्षर (स्व-प्रमाणित) करना, और आवेदन पूरा करने के लिए हस्ताक्षरित प्रति अपलोड करना आवश्यक है।",
      download_form: "फॉर्म डाउनलोड करें",
      downloading: "डाउनलोड हो रहा है...",
      save_application_first: "कृपया पहले आवेदन सहेजें",
    },
    marathi: {
      note: "सूचना",
      applicants_download_note:
        "अर्जदारांनी भरलेला अर्ज डाउनलोड करून, त्यावर स्वाक्षरी (स्व-साक्षांकित) करून, अर्ज पूर्ण करण्यासाठी स्वाक्षरी केलेली प्रत अपलोड करणे आवश्यक आहे.",
      download_form: "फॉर्म डाउनलोड करा",
      downloading: "डाउनलोड होत आहे...",
      save_application_first: "कृपया प्रथम अर्ज जतन करा",
    },
  };

  return translations[language]?.[key] || translations.english[key];
};

export function DownloadFormButton({ getValues, defaults, documentConfigs }: any) {
  const language = useLanguage();
  const application = useSelectedApplication();
  const [loading, setLoading] = useState(false);

  const hasBeenSaved = !!application?.id;

  const handleDownload = async () => {
    try {
      setLoading(true);

      const data = {
        ...defaults,
        ...getValues(),
        applicationId: application?.applicationId,
        id: application?.id,
      };

      const blob = await generateFilledPDFDocument(data, documentConfigs || []);
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Application_Form_${application?.applicationId || "Draft"}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
      SuccessToast("Form downloaded successfully!");
    } catch (e) {
      ErrorToast("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      disabled={!hasBeenSaved || loading}
      onClick={handleDownload}
      className="w-full sm:w-auto bg-blue-600 px-6 py-2.5 sm:py-2 rounded-full text-white font-bold text-sm sm:text-base disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
      title={
        !hasBeenSaved
          ? getTranslation("save_application_first", language)
          : ""
      }
    >
      {loading
        ? getTranslation("downloading", language)
        : getTranslation("download_form", language)}
    </button>
  );
}

export function ApplicationDownloadNote() {
  const language = useLanguage();

  return (
    <div className="mx-auto max-w-[800px] mt-6 mb-6">
      <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
        <p className="text-sm text-gray-700">
          <strong>{getTranslation("note", language)}:</strong>{" "}
          {getTranslation("applicants_download_note", language)}
        </p>
      </div>
    </div>
  );
}
